import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Avatar, Stack, Chip, Button, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';

import { handleInvitation, markAllAsRead, getNotifications } from '../../actions/notification';

moment.locale('es');

const BOOKING_TYPES = ['BOOKING_REQUEST', 'BOOKING_CONFIRMED', 'BOOKING_DECLINED', 'BOOKING_EXPIRED', 'BOOKING_CANCELLED',
  'BOOKING_MODIFICATION_REQUEST', 'BOOKING_MODIFICATION_ACCEPTED', 'BOOKING_MODIFICATION_REJECTED', 'BOOKING_MODIFIED'];

// Notificaciones de tickets/solicitudes: llevan a "Mis tickets" para dar seguimiento.
const TICKET_LINK_TYPES = ['TICKET_RESPUESTA', 'TICKET_ACTUALIZADO',
  'SOLICITUD_RECIBIDA', 'SOLICITUD_RESUELTA', 'SOLICITUD_NOVEDAD', 'SOLICITUD_NO_PROCEDE'];

// Vista de la página de reservas a la que apunta la notificación (guardada en data.rol)
const rutaReservas = (notif) => `/user/reservas-proveedor?vista=${notif.data?.rol === 'cliente' ? 'cliente' : 'proveedor'}`;

const getAvatarColor = (type) => {
  if (type === 'INVITATION_REJECTED' || type === 'BOOKING_DECLINED' || type === 'BOOKING_CANCELLED' || type === 'BOOKING_MODIFICATION_REJECTED') return '#FF827A';
  if (type === 'BOOKING_CONFIRMED' || type === 'BOOKING_MODIFICATION_ACCEPTED') return '#59C39A';
  if (type === 'BOOKING_MODIFICATION_REQUEST') return '#FFD074';
  if (type === 'BOOKING_MODIFIED' || type === 'BOOKING_DISPUTA_RESUELTA' || type === 'TICKET_RESPUESTA' || type === 'TICKET_ACTUALIZADO') return '#80A3FF';
  if (type === 'BOOKING_DISPUTA' || type === 'PAYMENT_FAILED' || type === 'PAYMENT_CHARGEBACK' || type === 'SOLICITUD_NO_PROCEDE') return '#FF827A';
  if (type === 'BOOKING_AUTOCOMPLETADA' || type === 'PAYMENT_CONFIRMED' || type === 'PAYMENT_RELEASED' || type === 'SOLICITUD_RECIBIDA' || type === 'SOLICITUD_RESUELTA') return '#59C39A';
  if (type === 'SOLICITUD_NOVEDAD') return '#FFD074';
  if (type === 'BOOKING_EXPIRED') return '#737791';
  return '#0E2465';
};

const getIcon = (type) => {
  if (type === 'PAGE_INVITATION') return <PersonAddIcon fontSize="small" />;
  if (type === 'BOOKING_REQUEST') return <CalendarMonthIcon fontSize="small" />;
  if (type === 'BOOKING_CONFIRMED') return <EventAvailableIcon fontSize="small" />;
  if (type === 'BOOKING_DECLINED' || type === 'BOOKING_CANCELLED' || type === 'BOOKING_MODIFICATION_REJECTED') return <EventBusyIcon fontSize="small" />;
  if (type === 'BOOKING_MODIFICATION_REQUEST' || type === 'BOOKING_MODIFICATION_ACCEPTED' || type === 'BOOKING_MODIFIED') return <EventAvailableIcon fontSize="small" />;
  if (type === 'BOOKING_EXPIRED') return <AccessTimeIcon fontSize="small" />;
  if (type === 'SOLICITUD_RECIBIDA' || type === 'SOLICITUD_RESUELTA') return <CheckCircleOutlineIcon fontSize="small" />;
  if (type === 'SOLICITUD_NOVEDAD') return <WarningAmberIcon fontSize="small" />;
  if (type === 'SOLICITUD_NO_PROCEDE') return <BlockIcon fontSize="small" />;
  if (type === 'TICKET_RESPUESTA' || type === 'TICKET_ACTUALIZADO') return <ChatBubbleOutlineOutlinedIcon fontSize="small" />;
  return <NotificationsNoneIcon fontSize="small" />;
};

const getTitle = (type) => {
  switch (type) {
    case 'PAGE_INVITATION': return 'Solicitud para ser facilitador';
    case 'INVITATION_ACCEPTED': return '¡Invitación Aceptada!';
    case 'INVITATION_REJECTED': return 'Invitación Rechazada';
    case 'BOOKING_REQUEST': return 'Nueva solicitud de reserva';
    case 'BOOKING_CONFIRMED': return '¡Reserva confirmada!';
    case 'BOOKING_DECLINED': return 'Reserva declinada';
    case 'BOOKING_CANCELLED': return 'Reserva cancelada por el cliente';
    case 'BOOKING_MODIFICATION_REQUEST': return 'Solicitud de cambio de fecha';
    case 'BOOKING_MODIFICATION_ACCEPTED': return 'Cambio de fecha aceptado';
    case 'BOOKING_MODIFICATION_REJECTED': return 'Cambio de fecha rechazado';
    case 'BOOKING_MODIFIED': return 'Fecha de reserva actualizada';
    case 'BOOKING_EXPIRED': return 'Reserva caducada';
    case 'BOOKING_AUTOCOMPLETADA': return 'Servicio completado';
    case 'BOOKING_DISPUTA': return 'Problema reportado';
    case 'BOOKING_DISPUTA_RESUELTA': return 'Disputa resuelta';
    case 'SOLICITUD_RECIBIDA': return 'Solicitud recibida';
    case 'SOLICITUD_RESUELTA': return 'Solicitud resuelta';
    case 'SOLICITUD_NOVEDAD': return 'Noticias sobre tu solicitud';
    case 'SOLICITUD_NO_PROCEDE': return 'Tu solicitud no procedió';
    case 'TICKET_RESPUESTA': return 'Respuesta a tu ticket';
    case 'TICKET_ACTUALIZADO': return 'Actualización de tu ticket';
    case 'PAYMENT_CONFIRMED': return 'Pago confirmado';
    case 'PAYMENT_REFERENCE': return 'Referencia de pago';
    case 'PAYMENT_FAILED': return 'Problema con un pago';
    case 'PAYMENT_REFUNDED': return 'Reembolso realizado';
    case 'PAYMENT_RELEASED': return 'Pago transferido';
    case 'PAYMENT_NO_CLABE': return 'Registra tu CLABE';
    case 'PAYMENT_ONBOARDING': return 'Conecta tu cuenta';
    case 'PAYMENT_CHARGEBACK': return 'Contracargo en revisión';
    case 'EXTRA_AGREGADO': return 'Extra agregado al servicio';
    case 'EXTRA_ELIMINADO': return 'Extra eliminado';
    default: return 'Notificación';
  }
};

const getSubtitle = (notif) => {
  switch (notif.type) {
    case 'PAGE_INVITATION': return 'Has recibido una solicitud para impartir un servicio.';
    case 'INVITATION_ACCEPTED': return `${notif.data?.userName} ha aceptado unirse a tu página.`;
    case 'INVITATION_REJECTED': return `${notif.data?.userName} ha declinado la invitación.`;
    case 'BOOKING_REQUEST': return `${notif.data?.userName} quiere reservar tu servicio.`;
    case 'BOOKING_CONFIRMED': return `${notif.data?.userName} confirmó tu reserva.`;
    case 'BOOKING_DECLINED': return `${notif.data?.userName} declinó tu solicitud.`;
    case 'BOOKING_CANCELLED': return `${notif.data?.userName} canceló su reserva.`;
    case 'BOOKING_MODIFICATION_REQUEST': return `${notif.data?.userName} quiere cambiar la fecha de la reserva.`;
    case 'BOOKING_MODIFICATION_ACCEPTED': return notif.data?.confirmada
      ? `${notif.data?.userName} aceptó tu propuesta de fecha y la reserva quedó confirmada.`
      : `${notif.data?.userName} aceptó tu solicitud de cambio de fecha.`;
    case 'BOOKING_MODIFICATION_REJECTED': return `${notif.data?.userName} rechazó tu solicitud de cambio de fecha.`;
    case 'BOOKING_MODIFIED': return `${notif.data?.userName} cambió la fecha de su reserva pendiente.`;
    case 'BOOKING_EXPIRED': return 'El proveedor no respondió a tiempo. Tu reserva fue cancelada automáticamente.';
    // Disputas, pagos y extras traen su texto en data.message
    default: return notif.data?.message || '';
  }
};

const NotifItem = ({ notif, onAccept, onReject, navigate, isLast }) => (
  <>
    <Box sx={{ p: 2.5, bgcolor: notif.read ? 'transparent' : 'rgba(116,149,248,0.04)' }}>
      <Stack direction="row" spacing={2}>
        <Avatar sx={{ bgcolor: getAvatarColor(notif.type), width: 44, height: 44, flexShrink: 0 }}>
          {getIcon(notif.type)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="body2" fontWeight="bold" color="primary.main">
              {getTitle(notif.type)}
            </Typography>
            {!notif.read && (
              <Box sx={{ width: 8, height: 8, bgcolor: 'primary.main', borderRadius: '50%', mt: 0.5, ml: 1, flexShrink: 0 }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: '#737791', display: 'block', mb: 1.5 }}>
            {getSubtitle(notif)}
          </Typography>

          {BOOKING_TYPES.includes(notif.type) && (
            <Box sx={{ bgcolor: '#F1F5F9', p: 1.5, borderRadius: 1, mb: 1.5 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <span style={{ color: '#737791' }}>Servicio:</span>
                  <span style={{ color: '#0E2465', fontWeight: 'bold' }}>{notif.data?.serviceName}</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <span style={{ color: '#737791' }}>Fecha:</span>
                  <span style={{ color: '#0E2465' }}>{notif.data?.fecha}</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <span style={{ color: '#737791' }}>Hora:</span>
                  <span style={{ color: '#0E2465' }}>{notif.data?.hora} hrs</span>
                </Typography>
                {notif.type === 'BOOKING_DECLINED' && notif.data?.motivo && (
                  <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                    <span style={{ color: '#737791' }}>Motivo:</span>
                    <span style={{ color: '#FF827A' }}>{notif.data.motivo}</span>
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {notif.type === 'PAGE_INVITATION' && notif.status !== 'PENDING' && (
            <Box sx={{ bgcolor: '#F1F5F9', p: 1.5, borderRadius: 1, mb: 1.5 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <span style={{ color: '#737791' }}>De:</span>
                  <span style={{ color: '#0E2465', fontWeight: 'bold' }}>{notif.data?.pageName}</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <span style={{ color: '#737791' }}>Servicio:</span>
                  <span style={{ color: '#0E2465' }}>{notif.data?.serviceName}</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <span style={{ color: '#737791' }}>Modalidad:</span>
                  <span style={{ color: '#0E2465' }}>{notif.data?.modality}</span>
                </Typography>
              </Stack>
            </Box>
          )}

          {(notif.type === 'BOOKING_REQUEST' || notif.type === 'BOOKING_MODIFICATION_REQUEST' || notif.type === 'BOOKING_MODIFIED') && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(rutaReservas(notif))}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: '#0a1b4d' } }}
              >
                {notif.type === 'BOOKING_MODIFIED' ? 'Ver reserva' : 'Ver solicitud'}
              </Button>
            </Box>
          )}

          {(notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_EXPIRED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={
                  notif.type === 'BOOKING_CONFIRMED' ? 'CONFIRMADA'
                  : notif.type === 'BOOKING_DECLINED' ? 'DECLINADA'
                  : notif.type === 'BOOKING_CANCELLED' ? 'CANCELADA'
                  : notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? 'CAMBIO ACEPTADO'
                  : notif.type === 'BOOKING_MODIFICATION_REJECTED' ? 'CAMBIO RECHAZADO'
                  : 'CADUCADA'
                }
                size="small"
                sx={{
                  bgcolor: (notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED') ? '#9DE1D5'
                    : (notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') ? 'rgba(255,130,122,0.15)'
                    : '#F1F5F9',
                  color: (notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED') ? '#0E2465'
                    : (notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') ? '#FF827A'
                    : '#737791',
                  fontWeight: 'bold',
                  borderRadius: 1
                }}
              />
            </Box>
          )}

          {notif.type === 'PAGE_INVITATION' && notif.status === 'PENDING' ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" size="small" onClick={() => onAccept(notif._id)}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: '#0a1b4d' } }}>
                Aceptar
              </Button>
              <Button variant="contained" size="small"
                onClick={() => navigate(`/pagina/${notif.data?.pageId}`)}
                sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: 1, fontSize: '11px' }}>
                Detalles
              </Button>
              <Button variant="contained" size="small" onClick={() => onReject(notif._id)}
                sx={{ bgcolor: 'error.main', textTransform: 'none', borderRadius: 1, fontSize: '11px' }}>
                Rechazar
              </Button>
            </Box>
          ) : notif.type === 'PAGE_INVITATION' ? (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={notif.status === 'ACCEPTED' ? 'ACEPTADA' : 'RECHAZADA'}
                size="small"
                sx={{
                  bgcolor: notif.status === 'ACCEPTED' ? '#9DE1D5' : 'rgba(255,130,122,0.15)',
                  color: notif.status === 'ACCEPTED' ? '#0E2465' : '#FF827A',
                  fontWeight: 'bold',
                  borderRadius: 1
                }}
              />
            </Box>
          ) : null}

          {TICKET_LINK_TYPES.includes(notif.type) && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(notif.data?.ticketId ? `/user/tickets?id=${notif.data.ticketId}` : '/user/tickets')}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: '#0a1b4d' } }}
              >
                Ver ticket
              </Button>
            </Box>
          )}

          <Typography variant="caption" sx={{ color: '#737791' }}>
            hace {moment(notif.date).fromNow(true)}
          </Typography>
        </Box>
      </Stack>
    </Box>
    {!isLast && <Divider />}
  </>
);

const SeccionNotificaciones = ({ titulo, subtitulo, notifs, onAccept, onReject, navigate, accion }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Box>
        <Typography variant="h6" fontWeight="bold" color="primary.main">{titulo}</Typography>
        <Typography variant="body2" sx={{ color: '#737791' }}>{subtitulo}</Typography>
      </Box>
      {accion}
    </Box>
    {notifs.length === 0 ? (
      <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 4, textAlign: 'center', border: '1px solid #F1F5F9' }}>
        <Typography variant="body2" sx={{ color: '#737791' }}>Sin notificaciones en esta sección</Typography>
      </Box>
    ) : (
      <Box sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid #F1F5F9', overflow: 'hidden' }}>
        {notifs.map((notif, i) => (
          <NotifItem
            key={notif._id}
            notif={notif}
            onAccept={onAccept}
            onReject={onReject}
            navigate={navigate}
            isLast={i === notifs.length - 1}
          />
        ))}
      </Box>
    )}
  </Box>
);

const NotificacionesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications } = useSelector(state => state.notifications);
  const [fechaCarga] = useState(moment().format('DD/MM/YYYY HH:mm'));

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  const nuevas = (notifications || []).filter(n => !n.read);
  const recientes = (notifications || []).filter(n => n.read);

  const onAccept = async (id) => {
    await dispatch(handleInvitation(id, 'ACCEPT'));
  };

  const onReject = async (id) => {
    await dispatch(handleInvitation(id, 'REJECT'));
  };

  const onMarcarTodas = () => {
    dispatch(markAllAsRead());
  };

  return (
    <Box className="main-page">
      <Box sx={{ bgcolor: '#7495f8', color: 'white', borderRadius: 2, px: 4, py: 5, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Centro de notificaciones</Typography>
          <Typography mt={1} variant="body2" sx={{ opacity: 0.85 }}>Conoce tus notificaciones</Typography>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5 }}>
          Fecha y hora de ultima actualización de datos: {fechaCarga} h
        </Typography>
      </Box>

      <SeccionNotificaciones
        titulo="Nuevas"
        subtitulo="Consulta tus nuevas notificaciones"
        notifs={nuevas}
        onAccept={onAccept}
        onReject={onReject}
        navigate={navigate}
        accion={nuevas.length > 0 && (
          <Button
            variant="text"
            size="small"
            onClick={onMarcarTodas}
            sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 'bold', fontSize: '12px' }}
          >
            Marcar todas como leídas
          </Button>
        )}
      />

      <SeccionNotificaciones
        titulo="Recientes"
        subtitulo="Consulta otras notificaciones"
        notifs={recientes}
        onAccept={onAccept}
        onReject={onReject}
        navigate={navigate}
      />
    </Box>
  );
};

export default NotificacionesPage;
